import { useState, useCallback } from 'react';
import './ApiDocs.css';

// Types
interface Parameter {
  name: string;
  in: 'path' | 'query' | 'body' | 'header';
  required: boolean;
  type: string;
  description: string;
  example?: string;
}

interface Response {
  status: number;
  description: string;
  example?: object;
}

interface Endpoint {
  id: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  summary: string;
  description: string;
  tags: string[];
  parameters: Parameter[];
  requestBody?: {
    type: string;
    example: object;
  };
  responses: Response[];
  authenticated: boolean;
}

interface ApiSection {
  name: string;
  description: string;
  endpoints: Endpoint[];
}

// Mock API Documentation
const API_SECTIONS: ApiSection[] = [
  {
    name: 'Authentication',
    description: 'Authenticate with Spark OAuth or API keys',
    endpoints: [
      {
        id: 'auth-spark',
        method: 'POST',
        path: '/api/auth/spark',
        summary: 'Authenticate with Spark',
        description: 'Exchange a Spark signature for an access token. The signature is created by scanning a Spark QR code.',
        tags: ['auth'],
        parameters: [],
        requestBody: {
          type: 'application/json',
          example: {
            spark_signature: 'base58...',
            challenge: 'random-challenge-string',
          },
        },
        responses: [
          { status: 200, description: 'Authentication successful', example: { token: 'eyJ...', expires_in: 3600 } },
          { status: 401, description: 'Invalid signature' },
        ],
        authenticated: false,
      },
      {
        id: 'auth-refresh',
        method: 'POST',
        path: '/api/auth/refresh',
        summary: 'Refresh access token',
        description: 'Exchange a refresh token for a new access token.',
        tags: ['auth'],
        parameters: [],
        requestBody: {
          type: 'application/json',
          example: { refresh_token: 'eyJ...' },
        },
        responses: [
          { status: 200, description: 'Token refreshed', example: { token: 'eyJ...', expires_in: 3600 } },
          { status: 401, description: 'Invalid refresh token' },
        ],
        authenticated: false,
      },
    ],
  },
  {
    name: 'Lattices',
    description: 'Manage compute lattices',
    endpoints: [
      {
        id: 'lattice-list',
        method: 'GET',
        path: '/api/lattices',
        summary: 'List all lattices',
        description: 'Returns a list of all lattices accessible to the authenticated user.',
        tags: ['lattices'],
        parameters: [
          { name: 'status', in: 'query', required: false, type: 'string', description: 'Filter by status (active, paused, error)', example: 'active' },
          { name: 'limit', in: 'query', required: false, type: 'integer', description: 'Maximum results', example: '10' },
        ],
        responses: [
          { status: 200, description: 'List of lattices', example: { lattices: [{ id: 'lat_abc', name: 'prod-lattice', status: 'active' }] } },
        ],
        authenticated: true,
      },
      {
        id: 'lattice-get',
        method: 'GET',
        path: '/api/lattices/{id}',
        summary: 'Get lattice details',
        description: 'Returns detailed information about a specific lattice.',
        tags: ['lattices'],
        parameters: [
          { name: 'id', in: 'path', required: true, type: 'string', description: 'Lattice ID' },
        ],
        responses: [
          { status: 200, description: 'Lattice details', example: { id: 'lat_abc', name: 'prod-lattice', nodes: 5, circuits: 12 } },
          { status: 404, description: 'Lattice not found' },
        ],
        authenticated: true,
      },
      {
        id: 'lattice-create',
        method: 'POST',
        path: '/api/lattices',
        summary: 'Create a new lattice',
        description: 'Creates a new compute lattice with the specified configuration.',
        tags: ['lattices'],
        parameters: [],
        requestBody: {
          type: 'application/json',
          example: {
            name: 'my-lattice',
            region: 'us-central1',
            min_nodes: 2,
            max_nodes: 10,
          },
        },
        responses: [
          { status: 201, description: 'Lattice created', example: { id: 'lat_xyz', name: 'my-lattice', status: 'provisioning' } },
          { status: 400, description: 'Invalid configuration' },
        ],
        authenticated: true,
      },
    ],
  },
  {
    name: 'Circuits',
    description: 'Deploy and execute circuits',
    endpoints: [
      {
        id: 'circuit-list',
        method: 'GET',
        path: '/api/circuits',
        summary: 'List all circuits',
        description: 'Returns a list of all deployed circuits.',
        tags: ['circuits'],
        parameters: [
          { name: 'lattice_id', in: 'query', required: false, type: 'string', description: 'Filter by lattice' },
        ],
        responses: [
          { status: 200, description: 'List of circuits', example: { circuits: [{ id: 'cir_abc', name: 'aggregate-v1', version: '1.2.3' }] } },
        ],
        authenticated: true,
      },
      {
        id: 'circuit-execute',
        method: 'POST',
        path: '/api/circuits/{id}/execute',
        summary: 'Execute a circuit',
        description: 'Executes a circuit with the provided input and returns the result with PoVC proof.',
        tags: ['circuits'],
        parameters: [
          { name: 'id', in: 'path', required: true, type: 'string', description: 'Circuit ID' },
        ],
        requestBody: {
          type: 'application/json',
          example: {
            input: { values: [1, 2, 3, 4, 5] },
            options: { timeout_ms: 5000 },
          },
        },
        responses: [
          { status: 200, description: 'Execution result', example: { output: { sum: 15, average: 3 }, proof: 'base58...', duration_ms: 42 } },
          { status: 400, description: 'Invalid input' },
          { status: 408, description: 'Execution timeout' },
        ],
        authenticated: true,
      },
      {
        id: 'circuit-deploy',
        method: 'POST',
        path: '/api/circuits',
        summary: 'Deploy a circuit',
        description: 'Deploys a new circuit to the specified lattice.',
        tags: ['circuits'],
        parameters: [],
        requestBody: {
          type: 'application/json',
          example: {
            name: 'my-circuit',
            version: '1.0.0',
            lattice_id: 'lat_abc',
            wasm_hash: 'sha256:...',
          },
        },
        responses: [
          { status: 201, description: 'Circuit deployed', example: { id: 'cir_xyz', status: 'active' } },
          { status: 400, description: 'Invalid circuit' },
        ],
        authenticated: true,
      },
    ],
  },
  {
    name: 'Governance',
    description: 'Governance proposals and signatures',
    endpoints: [
      {
        id: 'governance-proposals',
        method: 'GET',
        path: '/api/governance/proposals',
        summary: 'List pending proposals',
        description: 'Returns all pending governance proposals that require signatures.',
        tags: ['governance'],
        parameters: [
          { name: 'status', in: 'query', required: false, type: 'string', description: 'Filter by status (pending, approved, rejected)' },
        ],
        responses: [
          { status: 200, description: 'List of proposals', example: { proposals: [{ id: 'prop_abc', type: 'circuit_deploy', signatures: 2, threshold: 3 }] } },
        ],
        authenticated: true,
      },
      {
        id: 'governance-sign',
        method: 'POST',
        path: '/api/governance/proposals/{id}/sign',
        summary: 'Sign a proposal',
        description: 'Adds a signature to a governance proposal.',
        tags: ['governance'],
        parameters: [
          { name: 'id', in: 'path', required: true, type: 'string', description: 'Proposal ID' },
        ],
        requestBody: {
          type: 'application/json',
          example: {
            signature: 'base58...',
            key_hash: 'hash...',
          },
        },
        responses: [
          { status: 200, description: 'Signature added', example: { signatures: 3, threshold: 3, status: 'approved' } },
          { status: 400, description: 'Invalid signature' },
          { status: 409, description: 'Already signed' },
        ],
        authenticated: true,
      },
    ],
  },
  {
    name: 'Metrics',
    description: 'Network metrics and statistics',
    endpoints: [
      {
        id: 'metrics-network',
        method: 'GET',
        path: '/api/metrics/network',
        summary: 'Get network metrics',
        description: 'Returns current network health metrics.',
        tags: ['metrics'],
        parameters: [],
        responses: [
          { status: 200, description: 'Network metrics', example: { tps: 1250, latency_p99_ms: 45, nodes_online: 12, error_rate: 0.001 } },
        ],
        authenticated: true,
      },
      {
        id: 'metrics-tenant',
        method: 'GET',
        path: '/api/metrics/tenant',
        summary: 'Get tenant usage',
        description: 'Returns usage metrics for the current tenant.',
        tags: ['metrics'],
        parameters: [
          { name: 'range', in: 'query', required: false, type: 'string', description: 'Time range (1h, 24h, 7d, 30d)', example: '24h' },
        ],
        responses: [
          { status: 200, description: 'Tenant metrics', example: { transactions: 12345, compute_units: 987654, cost_usd: 45.67 } },
        ],
        authenticated: true,
      },
    ],
  },
];

// Code snippets
function generateCodeSnippet(endpoint: Endpoint, language: string): string {
  const baseUrl = 'https://api.estream.dev';
  const url = `${baseUrl}${endpoint.path}`;

  switch (language) {
    case 'curl':
      let curl = `curl -X ${endpoint.method} "${url}"`;
      if (endpoint.authenticated) {
        curl += ' \\\n  -H "Authorization: Bearer $TOKEN"';
      }
      if (endpoint.requestBody) {
        curl += ' \\\n  -H "Content-Type: application/json"';
        curl += ` \\\n  -d '${JSON.stringify(endpoint.requestBody.example, null, 2)}'`;
      }
      return curl;

    case 'javascript':
      let js = `const response = await fetch("${url}", {\n  method: "${endpoint.method}",\n  headers: {`;
      if (endpoint.authenticated) {
        js += '\n    "Authorization": `Bearer ${token}`,';
      }
      if (endpoint.requestBody) {
        js += '\n    "Content-Type": "application/json",';
      }
      js += '\n  },';
      if (endpoint.requestBody) {
        js += `\n  body: JSON.stringify(${JSON.stringify(endpoint.requestBody.example, null, 4).split('\n').join('\n  ')}),`;
      }
      js += '\n});\nconst data = await response.json();';
      return js;

    case 'python':
      let py = 'import requests\n\n';
      py += `response = requests.${endpoint.method.toLowerCase()}(\n    "${url}",`;
      if (endpoint.authenticated) {
        py += '\n    headers={"Authorization": f"Bearer {token}"},';
      }
      if (endpoint.requestBody) {
        py += `\n    json=${JSON.stringify(endpoint.requestBody.example, null, 4).split('\n').join('\n    ')},`;
      }
      py += '\n)\ndata = response.json()';
      return py;

    default:
      return '';
  }
}

// Components
function MethodBadge({ method }: { method: Endpoint['method'] }) {
  return <span className={`method-badge method-${method.toLowerCase()}`}>{method}</span>;
}

function EndpointCard({ endpoint, isExpanded, onToggle }: { endpoint: Endpoint; isExpanded: boolean; onToggle: () => void }) {
  const [codeLanguage, setCodeLanguage] = useState('curl');
  const [tryItInput, setTryItInput] = useState('');
  const [tryItResult, setTryItResult] = useState<string | null>(null);

  const handleTryIt = useCallback(() => {
    // Mock API call
    setTryItResult(JSON.stringify(endpoint.responses[0]?.example || { success: true }, null, 2));
  }, [endpoint]);

  return (
    <div className={`endpoint-card ${isExpanded ? 'expanded' : ''}`}>
      <div className="endpoint-header" onClick={onToggle}>
        <MethodBadge method={endpoint.method} />
        <span className="endpoint-path">{endpoint.path}</span>
        <span className="endpoint-summary">{endpoint.summary}</span>
        {endpoint.authenticated && <span className="auth-badge">Auth</span>}
        <span className="expand-icon">{isExpanded ? '−' : '+'}</span>
      </div>

      {isExpanded && (
        <div className="endpoint-content">
          <p className="endpoint-description">{endpoint.description}</p>

          {endpoint.parameters.length > 0 && (
            <div className="endpoint-section">
              <h4>Parameters</h4>
              <table className="params-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.parameters.map((param) => (
                    <tr key={param.name}>
                      <td className="param-name">{param.name}</td>
                      <td className="param-in">{param.in}</td>
                      <td className="param-type">{param.type}</td>
                      <td className="param-required">{param.required ? '✓' : ''}</td>
                      <td className="param-desc">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {endpoint.requestBody && (
            <div className="endpoint-section">
              <h4>Request Body</h4>
              <pre className="code-block">
                <code>{JSON.stringify(endpoint.requestBody.example, null, 2)}</code>
              </pre>
            </div>
          )}

          <div className="endpoint-section">
            <h4>Responses</h4>
            {endpoint.responses.map((response) => (
              <div key={response.status} className="response-item">
                <span className={`status-code status-${Math.floor(response.status / 100)}xx`}>
                  {response.status}
                </span>
                <span className="response-desc">{response.description}</span>
                {response.example && (
                  <pre className="code-block response-example">
                    <code>{JSON.stringify(response.example, null, 2)}</code>
                  </pre>
                )}
              </div>
            ))}
          </div>

          <div className="endpoint-section">
            <h4>Code Examples</h4>
            <div className="code-tabs">
              {['curl', 'javascript', 'python'].map((lang) => (
                <button
                  key={lang}
                  className={`code-tab ${codeLanguage === lang ? 'active' : ''}`}
                  onClick={() => setCodeLanguage(lang)}
                >
                  {lang}
                </button>
              ))}
            </div>
            <pre className="code-block">
              <code>{generateCodeSnippet(endpoint, codeLanguage)}</code>
            </pre>
          </div>

          <div className="endpoint-section try-it">
            <h4>Try It</h4>
            {endpoint.requestBody && (
              <textarea
                className="try-it-input"
                placeholder={JSON.stringify(endpoint.requestBody.example, null, 2)}
                value={tryItInput}
                onChange={(e) => setTryItInput(e.target.value)}
              />
            )}
            <button className="try-it-btn" onClick={handleTryIt}>
              Send Request
            </button>
            {tryItResult && (
              <pre className="code-block try-it-result">
                <code>{tryItResult}</code>
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ApiSection({ section, expandedEndpoints, onToggleEndpoint }: { section: ApiSection; expandedEndpoints: Set<string>; onToggleEndpoint: (id: string) => void }) {
  return (
    <div className="api-section" id={section.name.toLowerCase()}>
      <h2 className="section-title">{section.name}</h2>
      <p className="section-description">{section.description}</p>
      <div className="endpoints-list">
        {section.endpoints.map((endpoint) => (
          <EndpointCard
            key={endpoint.id}
            endpoint={endpoint}
            isExpanded={expandedEndpoints.has(endpoint.id)}
            onToggle={() => onToggleEndpoint(endpoint.id)}
          />
        ))}
      </div>
    </div>
  );
}

// Main Component
export default function ApiDocs() {
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const handleToggleEndpoint = useCallback((id: string) => {
    setExpandedEndpoints((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Filter sections and endpoints by search
  const filteredSections = API_SECTIONS.map((section) => ({
    ...section,
    endpoints: section.endpoints.filter(
      (e) =>
        e.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((s) => s.endpoints.length > 0);

  return (
    <div className="api-docs">
      <div className="docs-sidebar">
        <div className="sidebar-header">
          <h2>API Reference</h2>
          <span className="api-version">v1.0</span>
        </div>
        <input
          type="text"
          className="sidebar-search"
          placeholder="Search endpoints..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <nav className="sidebar-nav">
          {API_SECTIONS.map((section) => (
            <div key={section.name} className="nav-section">
              <a href={`#${section.name.toLowerCase()}`} className="nav-section-title">
                {section.name}
              </a>
              <div className="nav-endpoints">
                {section.endpoints.map((endpoint) => (
                  <a key={endpoint.id} href={`#${endpoint.id}`} className="nav-endpoint">
                    <MethodBadge method={endpoint.method} />
                    <span>{endpoint.path}</span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>

      <div className="docs-content">
        <div className="docs-header">
          <h1>eStream API Documentation</h1>
          <p>
            The eStream API provides programmatic access to lattices, circuits, governance, and metrics.
            All endpoints require authentication via Spark OAuth or API key.
          </p>
          <div className="base-url">
            <span className="label">Base URL:</span>
            <code>https://api.estream.dev</code>
          </div>
        </div>

        <div className="docs-sections">
          {filteredSections.map((section) => (
            <ApiSection
              key={section.name}
              section={section}
              expandedEndpoints={expandedEndpoints}
              onToggleEndpoint={handleToggleEndpoint}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
